/**
 * AnalyticsV2Widget — shared shell for the analytics-v2 marketing-lane
 * widgets (CORE-1041).
 *
 * Owns everything both widgets share:
 *   - Section wrapper + title header with PROD badge (plan §6.5 marker
 *     — marketing-lane widgets always show prod data, regardless of the
 *     dev/staging viewer's env context).
 *   - Window selector (7d / 30d / 90d). Widget-specific controls (e.g.
 *     funnel's source selector) go through the `extraControls` slot.
 *   - Loading / error / data-present render triad.
 *   - Attribution caption (from data.attributionWindow).
 *   - Source-degradation banner (from the pre-computed `degradedSources`
 *     prop — widgets pass this through `degradedSourceLabels` from
 *     utils/sourceStatuses.ts so the exhaustive-iteration invariant is
 *     shared, but the shell stays agnostic to the widget's specific
 *     SourceStatuses shape).
 *
 * The AnalyticsV2ErrorBoundary is NOT owned by the shell — each widget
 * wraps its `<AnalyticsV2Widget>` in an `<AnalyticsV2ErrorBoundary>`
 * directly. Wrapping at the widget level catches throws in the widget's
 * function body (before JSX renders — e.g. an unguarded property access
 * on a contract-drifted response), which a shell-owned boundary would
 * miss because the widget throws before the shell renders.
 *
 * Widgets pass their body as `children`; body is only rendered when data
 * is present AND error is null. An error replaces the body entirely
 * rather than stacking above/below stale data.
 */

import type { AttributionWindow, FunnelWindow } from '@/types/api';
import { attributionCaption } from '@/utils/attribution';
import { WidgetInfoButton } from './WidgetInfoButton';

const WINDOW_OPTIONS: readonly { value: FunnelWindow; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

/**
 * Minimum structural contract for the shell's data prop. Widgets pass a
 * response object that must at least carry an attributionWindow; the
 * per-source health map is passed separately as `degradedSources` so the
 * shell stays agnostic to widget-specific SourceStatuses interfaces
 * (which don't structurally satisfy Record<string, ...> in TypeScript).
 */
export interface AnalyticsV2WidgetData {
  attributionWindow: AttributionWindow;
}

interface Props<T extends AnalyticsV2WidgetData> {
  title: string;
  /**
   * Window selector state + handler. Optional so widgets whose primary
   * dimension isn't a time window (e.g. cohorts, which uses granularity)
   * can pass only `extraControls` and get a clean controls row without a
   * spurious 7d/30d/90d selector. When either is omitted the built-in
   * window `<select>` is not rendered.
   */
  selectedWindow?: FunnelWindow;
  onWindowChange?: (window: FunnelWindow) => void;
  data: T | null;
  error: string | null;
  isLoading: boolean;
  /**
   * Display labels for sources whose `.ok` is false. Widgets compute this
   * via `degradedSourceLabels(data.sourceStatuses)` from utils/sourceStatuses.ts
   * so the exhaustive-iteration invariant lives in one place.
   */
  degradedSources?: readonly string[];
  /**
   * Optional static advisory rendered above the body (e.g. "Early window —
   * informational only" for widgets whose ranking is unstable during a
   * ramp-up period). Rendered as `role="note"` (not `role="status"`) so
   * screen readers don't treat it as a live region — the copy is static
   * for the lifetime of the mounted widget.
   */
  rampUpNote?: React.ReactNode;
  /**
   * Optional click-triggered info popover appended to the title (CORE-1133).
   * Widget-specific vocabulary belongs here — what the columns mean, what
   * empty/degraded states signify, sign convention for deltas, etc. Rich
   * content (bold, code, links) is supported.
   */
  description?: React.ReactNode;
  extraControls?: React.ReactNode;
  children?: React.ReactNode;
}

export function AnalyticsV2Widget<T extends AnalyticsV2WidgetData>({
  title,
  selectedWindow,
  onWindowChange,
  data,
  error,
  isLoading,
  degradedSources = [],
  rampUpNote,
  description,
  extraControls,
  children,
}: Props<T>): JSX.Element {
  return (
    <section className="analytics-v2-widget">
      <header className="analytics-v2-widget-header">
        <h3>
          {title}
          {/* aria-hidden on the visible badge so its text doesn't get
           * concatenated into the h3's accessible name (heading a11y
           * name = descendant text). The sr-only sibling carries the
           * screen-reader announcement — matches QualityWidget's pattern. */}
          <span className="analytics-v2-widget-prod-badge" aria-hidden="true">
            PROD
          </span>
          {description !== undefined && (
            <WidgetInfoButton widgetName={title} description={description} />
          )}
        </h3>
        <span className="sr-only">Production data only</span>
      </header>

      <div className="analytics-v2-widget-controls">
        {selectedWindow !== undefined && onWindowChange !== undefined && (
          <label>
            Window
            <select
              className="analytics-v2-widget-select"
              value={selectedWindow}
              onChange={(e) => onWindowChange(e.target.value as FunnelWindow)}
            >
              {WINDOW_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        )}
        {extraControls}
      </div>

      {/* Hoisted above the loading gate so operators see the primer
          BEFORE they interpret the numbers. Suppressed on error because
          the primer contextualises numbers — when there are none and
          the operator's next action is "retry / read the error", a
          full-width info banner competes with the error CTA. */}
      {rampUpNote !== undefined && error === null && (
        <div className="analytics-v2-widget-rampup-note" role="note">
          {rampUpNote}
        </div>
      )}

      {isLoading && (
        <p className="analytics-v2-widget-loading">Loading {title.toLowerCase()} data…</p>
      )}

      {!isLoading && error !== null && (
        <div className="analytics-v2-widget-error" role="alert">
          Could not load {title.toLowerCase()} data: {error}
        </div>
      )}

      {/* Inline condition so TypeScript narrows `data` to non-null inside the block. */}
      {!isLoading && error === null && data !== null && (
        <>
          <p className="analytics-v2-widget-caption">
            Attribution: {attributionCaption(data.attributionWindow)}.
          </p>

          {degradedSources.length > 0 && (
            <p className="analytics-v2-widget-degradation" role="status">
              {degradedSources.join(', ')} unavailable — some columns may show dashes.
            </p>
          )}

          {children}
        </>
      )}
    </section>
  );
}
